import { useState, useEffect, Fragment } from 'react';
import { Button, LoadingSpinner, Input, Select } from '../../components/ui';
import { api } from '../../services/api-client';

interface DocLogRow {
  logId: string;
  documentId: string;
  action: string;
  readAt: string;
  userProfileId: string;
  userName: string | null;
  userEmail: string | null;
  fileName: string | null;
  tag: string | null;
  tagExtra: string | null;
  area: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  VIEW: 'Visualizzazione',
  DOWNLOAD: 'Download',
  ACKNOWLEDGE: 'Presa visione',
  PRINT: 'Stampa',
};

/**
 * Admin — Global document read log (document_read_log, all users).
 */
const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'Tutte le azioni' },
  { value: 'VIEW', label: 'Visualizzazione' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'ACKNOWLEDGE', label: 'Presa visione' },
  { value: 'PRINT', label: 'Stampa' },
];

const AREA_FILTER_OPTIONS = [
  { value: '', label: 'Tutte le aree' },
  { value: 'PRIVATE', label: 'Privata' },
  { value: 'PUBLIC', label: 'Pubblica' },
];

export function LogDocumentiPage() {
  const [entries, setEntries] = useState<DocLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [tagOptions, setTagOptions] = useState<{ value: string; label: string }[]>([{ value: '', label: 'Tutti i tipi' }]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [estimated, setEstimated] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (logId: string) => {
    setExpandedId((prev) => (prev === logId ? null : logId));
  };

  // Load distinct document types once for the filter dropdown
  useEffect(() => {
    api.get<{ data: string[] }>('/admin/logs/document-tags')
      .then((res) => {
        const opts = res.data.data.map((t) => ({ value: t, label: t }));
        setTagOptions([{ value: '', label: 'Tutti i tipi' }, ...opts]);
      })
      .catch(() => { /* keep default single option on failure */ });
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '30');
      if (search) params.set('search', search);
      if (actionFilter) params.set('action', actionFilter);
      if (areaFilter) params.set('area', areaFilter);
      if (tagFilter) params.set('tag', tagFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const response = await api.get<{
        data: DocLogRow[];
        pagination: { totalPages: number; totalItems: number; estimated?: boolean };
      }>(`/admin/logs/documents?${params.toString()}`);
      setEntries(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
      setEstimated(!!response.data.pagination.estimated);
    } catch {
      setEntries([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Reload when page changes or when a dropdown/date filter changes
  useEffect(() => { loadLogs(); }, [page, actionFilter, areaFilter, dateFrom, dateTo]);

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else loadLogs();
  };

  const resetFilters = () => {
    setSearch('');
    setActionFilter('');
    setAreaFilter('');
    setTagFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <div className="intranet-page page-wide">
      <div className="page-header">
        <h2>Log — Documenti</h2>
      </div>

      <div className="filters-panel">
        <div className="filters-grid">
          <Input
            label="Cerca"
            placeholder="Nome file o utente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <Select
            label="Tipo documento"
            options={tagOptions}
            value={tagFilter}
            onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
          />
          <Select
            label="Azione"
            options={ACTION_FILTER_OPTIONS}
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          />
          <Select
            label="Area"
            options={AREA_FILTER_OPTIONS}
            value={areaFilter}
            onChange={(e) => { setAreaFilter(e.target.value); setPage(1); }}
          />
          <Input
            label="Dal"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          />
          <Input
            label="Al"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          />
        </div>
        <div className="filters-actions">
          <Button variant="secondary" size="sm" onClick={applyFilters}>
            Cerca
          </Button>
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Azzera
          </Button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <p>Nessuna lettura documenti registrata.</p>
        </div>
      ) : (
        <>
          <div className="audit-table-wrapper">
            <table className="audit-table">
              <thead>
                <tr>
                  <th style={{ width: '32px' }}></th>
                  <th>Data</th>
                  <th>Utente</th>
                  <th>Azione</th>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th>Area</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const isExpanded = expandedId === entry.logId;
                  return (
                    <Fragment key={entry.logId}>
                      <tr
                        className={`audit-row audit-row--clickable${isExpanded ? ' audit-row--expanded' : ''}`}
                        onClick={() => toggleExpand(entry.logId)}
                      >
                        <td className="audit-expand-toggle">{isExpanded ? '▼' : '▶'}</td>
                        <td className="audit-date">
                          {new Date(entry.readAt).toLocaleString('it-IT', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </td>
                        <td>{entry.userName || entry.userEmail || '-'}</td>
                        <td>
                          <span className={`audit-action audit-action--${entry.action.toLowerCase()}`}>
                            {ACTION_LABELS[entry.action] || entry.action}
                          </span>
                        </td>
                        <td>{entry.fileName || '-'}</td>
                        <td>{entry.tag || '-'}</td>
                        <td>{entry.area || '-'}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="audit-detail-row">
                          <td colSpan={7}>
                            <div className="audit-detail-grid">
                              <DetailField label="Data/Ora" value={new Date(entry.readAt).toLocaleString('it-IT')} />
                              <DetailField label="Azione" value={ACTION_LABELS[entry.action] || entry.action} />
                              <DetailField label="Utente" value={entry.userName || '-'} />
                              <DetailField label="Email" value={entry.userEmail || '-'} />
                              <DetailField label="Documento" value={entry.fileName || '-'} />
                              <DetailField label="Tipo" value={entry.tag || '-'} />
                              <DetailField label="Dettaglio tipo" value={entry.tagExtra || '-'} />
                              <DetailField label="Area" value={entry.area || '-'} />
                              <DetailField label="Document ID" value={entry.documentId} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Paginazione log documenti">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                ← Precedente
              </Button>
              <span className="pagination-info">Pagina {page} di {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                Successiva →
              </Button>
            </div>
          )}

          {totalItems > 0 && (
            <p className="pagination-summary">
              {estimated ? `~${totalItems.toLocaleString('it-IT')} letture (stima)` : `${totalItems.toLocaleString('it-IT')} letture`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="audit-detail-field">
      <div className="audit-detail-label">{label}</div>
      <div className="audit-detail-value">{value}</div>
    </div>
  );
}
